import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, createBlock as _createBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "row items-center bg-white" }
const _hoisted_2 = {
  key: 0,
  class: "q-pr-sm"
}
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = {
  key: 0,
  class: "text-body1"
}

import SfxBtnIcon from "src/components/button/SfxBtnIcon.vue"
import SfxTrueCount from "src/components/tooltip/SfxTrueCount.vue"
import { Log } from "src/util/classes/log"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { NamedManagerPath } from "src/util/enums/namedPaths"
import { NavigationAction } from "src/util/enums/topNavigation"
import {
  BreadcrumbCount,
  BreadcrumbHTML,
  Breadcrumbs,
  BreadcrumbText,
} from "src/util/types/components/sfxBreadcrumb"
import { computed, PropType } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"

//region Props and Emits

type AllBreadcrumbs = BreadcrumbHTML | BreadcrumbText | BreadcrumbCount


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxBreadcrumbs',
  props: {
  breadcrumbs: {
    type: Array as PropType<Breadcrumbs>,
    required: true,
  },

  context: {
    type: String,
  },

  navigationAction: {
    type: Number as PropType<NavigationAction>,
    default: undefined,
  },

  isDialog: Boolean,
},
  emits: ["close"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

//endregion

//region Variables

const crumbs = computed<Breadcrumbs>(() => props.breadcrumbs)

const actionIcon = computed(() => {
  switch (props.navigationAction) {
    case NavigationAction.HISTORY_BACK:
      return Icons.CHEVRON_LEFT
    case NavigationAction.CLOSE:
      return Icons.CLOSE_LARGE
    default:
      return null
  }
})

const iconStyle = { "margin-top": "-12px" }

const { t } = useI18n()
const router = useRouter()

//endregion

//region Functions

function isBreadcrumbHTML(breadcrumb: AllBreadcrumbs): breadcrumb is BreadcrumbHTML {
  return "html" in breadcrumb
}

function isBreadcrumbText(breadcrumb: AllBreadcrumbs): breadcrumb is BreadcrumbText {
  return "text" in breadcrumb
}

function isBreadcrumbCount(breadcrumb: AllBreadcrumbs): breadcrumb is BreadcrumbCount {
  return "current" in breadcrumb && "total" in breadcrumb && "entity" in breadcrumb
}

/**
 * Determines the class (color of text) based on whether it is a link and if the text should be highlighted.
 * Text highlight takes precedence over the text being a link.
 *
 * @param link
 * @param highlight
 */
function getTextColorClass(link: boolean, highlight?: boolean) {
  const highlightClass = "text-info"
  const normalTextClass = "text-navy-base"

  if (link) {
    if (highlight === undefined || highlight) {
      return highlightClass
    } else if (!highlight) {
      return normalTextClass
    }
  } else if (highlight) {
    return highlightClass
  } else {
    return normalTextClass
  }
}

function actionClicked() {
  // If the breadcrumb is in a dialog
  if (props.isDialog) {
    emit("close")
    return
  }

  const isSafeExUrl = Boolean(router.options.history.state.back)

  if (props.navigationAction === NavigationAction.HISTORY_BACK && !isSafeExUrl) {
    // Do it backwards so that we can go up the chain
    for (let i = crumbs.value.length - 1; i >= 0; i--) {
      const crumb = crumbs.value[i]
      if ((isBreadcrumbHTML(crumb) || isBreadcrumbText(crumb)) && crumb.link) {
        return router.push(crumb.link)
      }
    }

    // The developer screwed up the breadcrumbs, and it is not possible to go back.
    // This should not happen, but in any case, just trigger history back.
    Log.error({ message: "Bad routes provided", data: { routes: crumbs.value } })
    router.back()
  } else if (props.navigationAction === NavigationAction.CLOSE) {
    router.back()
  } else if (isSafeExUrl) {
    router.back()
  } else {
    router.push({ name: NamedManagerPath.WORKSPACE })
  }
}

function getCountText(count: number, total: number, entity: string) {
  const _entity = t(`entity.${entity}`)
  return t("sfxBreadcrumbs.entityCount", { count, total, entity: _entity })
}

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_breadcrumbs_el = _resolveComponent("q-breadcrumbs-el")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_breadcrumbs = _resolveComponent("q-breadcrumbs")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (actionIcon.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(SfxBtnIcon, {
            icon: actionIcon.value,
            onClick: actionClicked,
            padding: "none"
          }, null, 8, ["icon"])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_q_breadcrumbs, { class: "text-subtitle1" }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(crumbs.value, (breadcrumb, index) => {
          return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
            (isBreadcrumbCount(breadcrumb))
              ? (_openBlock(), _createBlock(_component_q_breadcrumbs_el, { key: 0 }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", {
                      innerHTML: getCountText(breadcrumb.current, breadcrumb.total, breadcrumb.entity)
                    }, null, 8, _hoisted_3),
                    (breadcrumb?.trueTotal)
                      ? (_openBlock(), _createBlock(SfxTrueCount, {
                          key: 0,
                          "true-total": breadcrumb?.trueTotal,
                          "tooltip-data": breadcrumb?.tooltipContent
                        }, null, 8, ["true-total", "tooltip-data"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1024))
              : (isBreadcrumbText(breadcrumb))
                ? (_openBlock(), _createBlock(_component_q_breadcrumbs_el, {
                    key: 1,
                    label: breadcrumb.text,
                    to: breadcrumb.link,
                    class: _normalizeClass([getTextColorClass(Boolean(breadcrumb.link), breadcrumb.highlight), "row"])
                  }, {
                    default: _withCtx(() => [
                      (breadcrumb.icon)
                        ? (_openBlock(), _createBlock(_component_q_icon, {
                            key: 0,
                            name: breadcrumb.icon,
                            class: "q-pl-md",
                            size: "sm",
                            color: "inactive",
                            style: iconStyle
                          }, null, 8, ["name"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 2
                  }, 1032, ["label", "to", "class"]))
                : (isBreadcrumbHTML(breadcrumb))
                  ? (_openBlock(), _createBlock(_component_q_breadcrumbs_el, { key: 2 }, {
                      default: _withCtx(() => [
                        _createElementVNode("span", {
                          innerHTML: breadcrumb.html
                        }, null, 8, _hoisted_4)
                      ]),
                      _: 2
                    }, 1024))
                  : _createCommentVNode("", true)
          ], 64))
        }), 128)),
        (props.context)
          ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(props.context), 1))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})